import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiAlertTriangle,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiClipboard,
  FiDollarSign,
  FiMail,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiSend,
  FiTrendingDown,
  FiX,
} from 'react-icons/fi';
import {
  createInventoryCount,
  createInventoryMerma,
  finalizeInventoryCount,
  getCategories,
  getInventoryCountSession,
  getInventoryCountSessions,
  getInventoryEmailSuggestions,
  getInventoryMovements,
  getInventorySummary,
  sendInventoryEmail,
  updateInventoryCount,
} from '../api';
import InlineLoading from '../components/InlineLoading';
import SearchMultiSelect from '../components/SearchMultiSelect';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { notify } from '../utils/alertBus';
import { normalizePaginatedResponse } from '../utils/pagination';
import {
  formatInventoryCurrency,
  formatInventoryQuantity,
  formatKg,
  formatStockedUnits,
  getCountSessionStatusMeta,
  getInventoryProductImage,
  getInventoryKgPreview,
  getInventoryStatusMeta,
  getInventoryVariancePreview,
} from '../utils/inventoryFormatting';

const PAGE_SIZE = 25;
const PLACEHOLDER_IMAGE = 'https://placehold.co/96';
const INVENTORY_STATUS_LABELS = {
  all: 'Todos',
  ok: 'OK',
  negative: 'Negativo',
  warning: 'Revisar',
};

const getRowLabel = (row) =>
  [row?.productName, row?.varietyName].filter(Boolean).join(' - ') || `Variedad ${row?.varietyId || ''}`;

const getDefaultUnit = (row) =>
  row?.unitOptions?.find((option) => option.metricUnitKey === 'kg')?.metricUnit
  || row?.unitOptions?.[0]?.metricUnit
  || 'kg';

const getUserDisplayName = (user) => {
  if (!user) return 'Usuario actual';
  return user.name || user.username || `Usuario ${user.id}`;
};

const isQuantityEntered = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const hasPositiveQuantity = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const toDateTimeLocalValue = (value = new Date()) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part) => String(part).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoDateTime = (value) => {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('es-CL');
};

const fetchAllInventoryRows = async (branchId) => {
  const response = await getInventorySummary(branchId, {
    pageSize: 0,
    status: 'all',
  });
  const normalized = normalizePaginatedResponse(response);
  return normalized.data || [];
};

const buildEntryItems = ({ entries, rowsById, requirePositive = false, allowedVarietyIds = null }) =>
  Object.entries(entries)
    .filter(([varietyId]) => !allowedVarietyIds || allowedVarietyIds.has(Number(varietyId)))
    .filter(([, entry]) => (
      requirePositive
        ? hasPositiveQuantity(entry.quantity)
        : isQuantityEntered(entry.quantity)
    ))
    .map(([varietyId, entry]) => {
      const row = rowsById.get(Number(varietyId));
      return {
        varietyId: Number(varietyId),
        quantity: Number(entry.quantity),
        metricUnit: entry.metricUnit || getDefaultUnit(row),
        reason: entry.reason,
        note: entry.note,
      };
    });

const findMissingConversion = ({ items, rowsById }) =>
  items.find((item) => {
    const row = rowsById.get(Number(item.varietyId));
    return getInventoryKgPreview(row, item.quantity, item.metricUnit) === null;
  });

const getCategoryLabel = (category) => category?.name || category?.label || `Categoria ${category?.id}`;

const toCategoryOption = (category) => ({
  id: category.id,
  label: getCategoryLabel(category),
  name: getCategoryLabel(category),
});

const getRowCategoryId = (row) => (row?.categoryId === undefined || row?.categoryId === null ? '' : String(row.categoryId));

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const isValidEmail = (value) => EMAIL_PATTERN.test(normalizeEmail(value));

const toEmailOption = (entry) => {
  const email = normalizeEmail(entry?.email || entry?.username || entry?.id || entry);
  if (!isValidEmail(email)) return null;
  const name = entry?.name || '';
  return {
    id: email,
    email,
    name,
    source: entry?.source || 'manual',
    label: name ? `${name} <${email}>` : email,
  };
};

const ProductIdentity = ({ row, imageSize = 'h-14 w-14' }) => (
  <div className="flex items-start gap-3">
    <img
      src={getInventoryProductImage(row)}
      alt={row?.varietyName || row?.productName || 'Producto'}
      onError={(event) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = PLACEHOLDER_IMAGE;
      }}
      className={`${imageSize} flex-none rounded border border-slate-200 bg-slate-50 object-cover`}
    />
    <div className="min-w-0">
      <p className="font-semibold text-slate-900 break-words">{row?.productName || 'Producto'}</p>
      <p className="text-xs text-slate-500 break-words">Variedad: {row?.varietyName || 'Variedad'}</p>
      {row?.categoryName && (
        <p className="text-xs text-slate-400 break-words">{row.categoryName}</p>
      )}
    </div>
  </div>
);

const DetailLine = ({ label, value, className = '' }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <div className={`mt-0.5 text-sm text-slate-900 ${className}`}>{value}</div>
  </div>
);

const CategoryMultiSelect = ({
  categories = [],
  selectedCategoryIds = [],
  onChange,
  label = 'Categorias',
  placeholder = 'Buscar categoria',
  disabled = false,
}) => {
  const options = useMemo(() => categories.map(toCategoryOption), [categories]);
  const selected = useMemo(() => {
    const selectedSet = new Set((selectedCategoryIds || []).map(String));
    return options.filter((option) => selectedSet.has(String(option.id)));
  }, [options, selectedCategoryIds]);

  return (
    <SearchMultiSelect
      label={label}
      placeholder={placeholder}
      value={selected}
      options={options}
      disabled={disabled}
      getOptionLabel={(option) => option.label || option.name || String(option.id)}
      onChange={(next) => onChange?.((next || []).map((option) => String(option.id)))}
    />
  );
};

const CountSessionsPanel = ({ sessions, loading, onOpen }) => (
  <div className="bg-white border border-slate-200 rounded-lg">
    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Conteos</h2>
        <p className="text-xs text-slate-500">Listas fechadas con responsable registrado</p>
      </div>
      <FiCalendar className="text-slate-400" />
    </div>
    <div className="divide-y divide-slate-200">
      {loading ? (
        <div className="p-4"><InlineLoading label="Cargando conteos" /></div>
      ) : sessions.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">Sin conteos registrados.</p>
      ) : sessions.map((session) => {
        const statusMeta = getCountSessionStatusMeta(session);
        return (
          <button
            key={session.id}
            type="button"
            onClick={() => onOpen(session)}
            className="w-full p-4 text-left hover:bg-slate-50"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-semibold text-slate-900">{session.name || `Conteo #${session.id}`}</p>
                <p className="text-xs text-slate-500">
                  {formatDateTime(session.countedAt)} - {getUserDisplayName(session.responsibleUser)}
                </p>
              </div>
              <span className={`inline-flex w-fit items-center rounded-full border px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                {statusMeta.label}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
              <span>{session.itemCount || 0} filas</span>
              <span className="text-red-700">Falta {formatKg(session.missingKg)}</span>
              <span className="text-emerald-700">Sobra {formatKg(session.excessKg)}</span>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const InventoryEmailModal = ({
  branchId,
  search,
  status,
  selectedCategoryIds = [],
  categories = [],
  totalRows,
  kpis = {},
  onClose,
}) => {
  const [recipients, setRecipients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [sending, setSending] = useState(false);

  const categoryLabels = useMemo(() => {
    if (!selectedCategoryIds.length) return ['Todas'];
    const categoryMap = new Map((categories || []).map((category) => [String(category.id), getCategoryLabel(category)]));
    return selectedCategoryIds.map((id) => categoryMap.get(String(id)) || `Categoria ${id}`);
  }, [categories, selectedCategoryIds]);

  const loadSuggestions = useCallback(async (query = '') => {
    if (!branchId) return [];
    const response = await getInventoryEmailSuggestions(branchId, {
      search: query,
      limit: 20,
    });
    const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    return list.map(toEmailOption).filter(Boolean);
  }, [branchId]);

  useEffect(() => {
    let active = true;
    setLoadingSuggestions(true);
    loadSuggestions('')
      .then((items) => {
        if (active) setSuggestions(items);
      })
      .catch(() => {
        if (active) setSuggestions([]);
      })
      .finally(() => {
        if (active) setLoadingSuggestions(false);
      });
    return () => {
      active = false;
    };
  }, [loadSuggestions]);

  const handleSend = async () => {
    const emails = recipients
      .map((entry) => normalizeEmail(entry.email || entry.id))
      .filter(isValidEmail);
    if (!emails.length) {
      notify('Agrega al menos un correo válido.');
      return;
    }
    setSending(true);
    try {
      await sendInventoryEmail(branchId, {
        recipients: emails,
        filters: {
          search,
          status,
          categoryIds: selectedCategoryIds,
        },
      });
      notify('Correo de inventario enviado.');
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo enviar el correo de inventario.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Enviar reporte</p>
            <h2 className="text-xl font-semibold text-slate-900">Inventario en PDF</h2>
            <p className="text-sm text-slate-600">Se enviará con los filtros actuales de sucursal y categorías.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Cerrar"
          >
            <FiX />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
            <DetailLine label="Sucursal" value={`Sucursal ${branchId}`} />
            <DetailLine label="Estado" value={INVENTORY_STATUS_LABELS[status] || status || 'Todos'} />
            <DetailLine label="Categorias" value={categoryLabels.join(', ')} className="break-words" />
            <DetailLine label="Busqueda" value={search || 'Sin busqueda'} className="break-words" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filas</p>
              <p className="text-xl font-bold text-slate-900">{totalRows || 0}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Disponible</p>
              <p className="text-xl font-bold text-slate-900">{formatKg(kpis.availableKg)}</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mayorista</p>
              <p className="text-xl font-bold text-emerald-700">{formatInventoryCurrency(kpis.wholesaleEstimateAmount)}</p>
            </div>
          </div>

          <div>
            <SearchMultiSelect
              label="Destinatarios"
              placeholder={loadingSuggestions ? 'Cargando sugerencias...' : 'Buscar o escribir correo'}
              value={recipients}
              options={suggestions}
              onChange={setRecipients}
              onSearch={loadSuggestions}
              getOptionLabel={(option) => option.label || option.email || option.id}
              allowCreate
              createLabel={(query) => `Enviar a ${query}`}
              onCreateOption={(query) => {
                if (!isValidEmail(query)) {
                  notify('Ingresa un correo válido.');
                  return null;
                }
                return toEmailOption(query);
              }}
            />
            <p className="mt-2 text-xs text-slate-500">
              Las sugerencias combinan correos usados antes para inventario y usuarios de esta sucursal.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:bg-slate-400"
          >
            <FiSend /> {sending ? 'Enviando...' : 'Enviar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const InventoryCountWorkspace = ({
  branchId,
  sessionId,
  user,
  categoryOptions = [],
  initialCategoryIds = [],
  onClose,
  onSaved,
}) => {
  const [inventoryRows, setInventoryRows] = useState([]);
  const [session, setSession] = useState(null);
  const [entries, setEntries] = useState({});
  const [meta, setMeta] = useState({
    name: `Conteo ${new Date().toLocaleDateString('es-CL')}`,
    countedAt: toDateTimeLocalValue(),
    note: '',
  });
  const [search, setSearch] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() =>
    sessionId ? [] : (initialCategoryIds || []).map(String)
  );
  const [expandedVarietyId, setExpandedVarietyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const rowsById = useMemo(
    () => new Map(inventoryRows.map((row) => [Number(row.varietyId), row])),
    [inventoryRows]
  );
  const readOnly = session?.status === 'completed';
  const responsible = session?.responsibleUser || user;

  const loadWorkspace = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const [allRows, countResponse] = await Promise.all([
        fetchAllInventoryRows(branchId),
        sessionId ? getInventoryCountSession(branchId, sessionId) : Promise.resolve(null),
      ]);
      setInventoryRows(allRows);
      const loadedSession = countResponse?.session || null;
      setSession(loadedSession);
      if (loadedSession) {
        setSelectedCategoryIds([]);
        const nextEntries = {};
        (loadedSession.items || []).forEach((item) => {
          const row = allRows.find((entry) => Number(entry.varietyId) === Number(item.varietyId));
          nextEntries[item.varietyId] = {
            quantity: item.inputQuantity ?? '',
            metricUnit: item.inputMetricUnit || getDefaultUnit(row),
            note: item.note || '',
          };
        });
        setEntries(nextEntries);
        setMeta({
          name: loadedSession.name || `Conteo #${loadedSession.id}`,
          countedAt: toDateTimeLocalValue(loadedSession.countedAt),
          note: loadedSession.note || '',
        });
      }
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo cargar el conteo.');
    } finally {
      setLoading(false);
    }
  }, [branchId, sessionId]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const categoryScopedRows = useMemo(() => {
    const selectedSet = new Set((selectedCategoryIds || []).map(String));
    if (!selectedSet.size) return inventoryRows;
    return inventoryRows.filter((row) => selectedSet.has(getRowCategoryId(row)));
  }, [inventoryRows, selectedCategoryIds]);

  const scopedVarietyIds = useMemo(
    () => new Set(categoryScopedRows.map((row) => Number(row.varietyId))),
    [categoryScopedRows]
  );

  const filteredRows = useMemo(() => {
    const term = normalizeSearch(search);
    if (!term) return categoryScopedRows;
    return categoryScopedRows.filter((row) =>
      normalizeSearch(`${row.productName || ''} ${row.varietyName || ''}`).includes(term)
    );
  }, [categoryScopedRows, search]);

  const updateEntry = (row, patch) => {
    if (readOnly) return;
    setEntries((prev) => ({
      ...prev,
      [row.varietyId]: {
        quantity: '',
        metricUnit: getDefaultUnit(row),
        note: '',
        ...(prev[row.varietyId] || {}),
        ...patch,
      },
    }));
  };

  const serializeItems = () => buildEntryItems({
    entries,
    rowsById,
    requirePositive: false,
    allowedVarietyIds: scopedVarietyIds,
  });

  const persistDraft = async ({ showNotification = true } = {}) => {
    const items = serializeItems();
    const missingConversion = findMissingConversion({ items, rowsById });
    if (missingConversion) {
      notify(`No hay conversion a kg para ${missingConversion.metricUnit}.`);
      return null;
    }
    const payload = {
      name: meta.name,
      countedAt: toIsoDateTime(meta.countedAt),
      note: meta.note,
      items,
    };
    setSaving(true);
    try {
      const response = session?.id
        ? await updateInventoryCount(branchId, session.id, payload)
        : await createInventoryCount(branchId, payload);
      setSession(response.session);
      if (showNotification) notify('Borrador de conteo guardado.');
      onSaved?.();
      return response.session;
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo guardar el conteo.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (readOnly) return;
    await persistDraft();
  };

  const handleFinalize = async () => {
    if (readOnly) return;
    const items = serializeItems();
    if (!items.length) {
      notify('Agrega al menos una fila con cantidad contada.');
      return;
    }
    setFinalizing(true);
    try {
      const draft = await persistDraft({ showNotification: false });
      if (!draft?.id) return;
      await finalizeInventoryCount(branchId, draft.id);
      notify('Conteo finalizado.');
      onSaved?.();
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo finalizar el conteo.');
    } finally {
      setFinalizing(false);
    }
  };

  const enteredCount = serializeItems().length;

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-7xl bg-white rounded-lg shadow-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lista de conteo</p>
            <h2 className="text-xl font-semibold text-slate-900">
              {session?.id ? meta.name || `Conteo #${session.id}` : 'Nuevo conteo'}
            </h2>
            <p className="text-sm text-slate-600">Responsable: {getUserDisplayName(responsible)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <FiX /> Cerrar
            </button>
            {!readOnly && (
              <>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <FiSave /> {saving ? 'Guardando...' : 'Guardar borrador'}
                </button>
                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={saving || finalizing}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:bg-slate-400"
                >
                  <FiCheck /> {finalizing ? 'Finalizando...' : 'Finalizar conteo'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 grid grid-cols-1 lg:grid-cols-[1fr_220px_1fr] gap-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Nombre</label>
            <input
              value={meta.name}
              onChange={(event) => setMeta((prev) => ({ ...prev, name: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Fecha</label>
            <input
              type="datetime-local"
              value={meta.countedAt}
              onChange={(event) => setMeta((prev) => ({ ...prev, countedAt: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Nota</label>
            <input
              value={meta.note}
              onChange={(event) => setMeta((prev) => ({ ...prev, note: event.target.value }))}
              disabled={readOnly}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 disabled:bg-slate-100"
            />
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px_auto] lg:items-start">
          <label className="relative w-full md:max-w-md">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
              placeholder="Buscar producto o variedad"
            />
          </label>
          <CategoryMultiSelect
            categories={categoryOptions}
            selectedCategoryIds={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
            disabled={readOnly}
            label=""
            placeholder="Filtrar categorias"
          />
          <div className="text-sm text-slate-600 lg:text-right">
            {enteredCount} filas con conteo / {categoryScopedRows.length} variedades
          </div>
        </div>

        <div className="md:hidden divide-y divide-slate-200">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <InlineLoading label="Cargando inventario" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No hay productos para mostrar.
            </div>
          ) : filteredRows.map((row, index) => {
            const entry = entries[row.varietyId] || {
              quantity: '',
              metricUnit: getDefaultUnit(row),
              note: '',
            };
            const entered = isQuantityEntered(entry.quantity);
            const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
            const variance = entered ? getInventoryVariancePreview(row, entry.quantity, entry.metricUnit) : null;
            const unitOptions = row.unitOptions?.length
              ? row.unitOptions
              : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
            const isExpanded = String(expandedVarietyId) === String(row.varietyId);
            return (
              <article key={row.varietyId} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                <button
                  type="button"
                  onClick={() => setExpandedVarietyId(isExpanded ? null : row.varietyId)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                >
                  <ProductIdentity row={row} imageSize="h-12 w-12" />
                  <div className="flex shrink-0 items-center gap-2 text-right">
                    <div>
                      <p className="text-[11px] text-slate-500">Esperado</p>
                      <p className="text-sm font-semibold text-slate-900">{formatKg(row.availableKg)}</p>
                    </div>
                    {isExpanded ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <DetailLine label="Unidades" value={formatStockedUnits(row.stockedUnits)} />
                      <DetailLine label="Equiv. kg" value={
                        entered && kgPreview === null ? (
                          <span className="inline-flex items-center gap-1 text-amber-700"><FiAlertTriangle /> Sin conversion</span>
                        ) : entered ? formatKg(kgPreview) : '-'
                      } />
                      <DetailLine
                        label="Falta / sobra"
                        className={Number(variance) < 0 ? 'font-semibold text-red-700' : 'font-semibold text-emerald-700'}
                        value={entered && variance !== null ? formatKg(variance) : '-'}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Conteo
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={entry.quantity}
                          onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-right text-sm disabled:bg-slate-100"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Unidad
                        <select
                          value={entry.metricUnit}
                          onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100"
                        >
                          {unitOptions.map((option) => (
                            <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                              {option.metricUnit}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nota
                        <input
                          value={entry.note}
                          onChange={(event) => updateEntry(row, { note: event.target.value })}
                          disabled={readOnly}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm disabled:bg-slate-100"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Producto</th>
                <th className="px-4 py-3 text-right">Esperado</th>
                <th className="px-4 py-3 text-left">Unidades</th>
                <th className="px-4 py-3 text-right">Conteo</th>
                <th className="px-4 py-3 text-left">Unidad</th>
                <th className="px-4 py-3 text-right">Equiv. kg</th>
                <th className="px-4 py-3 text-right">Falta / sobra</th>
                <th className="px-4 py-3 text-left">Nota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <InlineLoading label="Cargando inventario" />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    No hay productos para mostrar.
                  </td>
                </tr>
              ) : filteredRows.map((row) => {
                const entry = entries[row.varietyId] || {
                  quantity: '',
                  metricUnit: getDefaultUnit(row),
                  note: '',
                };
                const entered = isQuantityEntered(entry.quantity);
                const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
                const variance = entered ? getInventoryVariancePreview(row, entry.quantity, entry.metricUnit) : null;
                const unitOptions = row.unitOptions?.length
                  ? row.unitOptions
                  : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
                return (
                  <tr key={row.varietyId} className="odd:bg-white even:bg-slate-50/70 hover:bg-slate-100">
                    <td className="px-4 py-3 min-w-56">
                      <ProductIdentity row={row} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{formatKg(row.availableKg)}</td>
                    <td className="px-4 py-3 text-slate-700 min-w-52">{formatStockedUnits(row.stockedUnits)}</td>
                    <td className="px-4 py-3 min-w-32">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={entry.quantity}
                        onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-right disabled:bg-slate-100"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-36">
                      <select
                        value={entry.metricUnit}
                        onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 disabled:bg-slate-100"
                      >
                        {unitOptions.map((option) => (
                          <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                            {option.metricUnit}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entered && kgPreview === null ? (
                        <span className="inline-flex items-center justify-end gap-1 text-amber-700">
                          <FiAlertTriangle /> Sin conversion
                        </span>
                      ) : entered ? formatKg(kgPreview) : '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${Number(variance) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {entered && variance !== null ? formatKg(variance) : '-'}
                    </td>
                    <td className="px-4 py-3 min-w-56">
                      <input
                        value={entry.note}
                        onChange={(event) => updateEntry(row, { note: event.target.value })}
                        disabled={readOnly}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 disabled:bg-slate-100"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BulkMermaWorkspace = ({ branchId, onClose, onSaved }) => {
  const [inventoryRows, setInventoryRows] = useState([]);
  const [entries, setEntries] = useState({});
  const [search, setSearch] = useState('');
  const [expandedVarietyId, setExpandedVarietyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const rowsById = useMemo(
    () => new Map(inventoryRows.map((row) => [Number(row.varietyId), row])),
    [inventoryRows]
  );

  useEffect(() => {
    const loadRows = async () => {
      setLoading(true);
      try {
        setInventoryRows(await fetchAllInventoryRows(branchId));
      } catch (error) {
        notify(error?.response?.data?.message || 'No se pudo cargar inventario.');
      } finally {
        setLoading(false);
      }
    };
    if (branchId) loadRows();
  }, [branchId]);

  const filteredRows = useMemo(() => {
    const term = normalizeSearch(search);
    if (!term) return inventoryRows;
    return inventoryRows.filter((row) =>
      normalizeSearch(`${row.productName || ''} ${row.varietyName || ''}`).includes(term)
    );
  }, [inventoryRows, search]);

  const updateEntry = (row, patch) => {
    setEntries((prev) => ({
      ...prev,
      [row.varietyId]: {
        quantity: '',
        metricUnit: getDefaultUnit(row),
        reason: 'Merma',
        note: '',
        ...(prev[row.varietyId] || {}),
        ...patch,
      },
    }));
  };

  const serializeItems = () => buildEntryItems({
    entries,
    rowsById,
    requirePositive: true,
  });

  const handleSave = async () => {
    const items = serializeItems();
    if (!items.length) {
      notify('Agrega al menos una fila con merma.');
      return;
    }
    const missingConversion = findMissingConversion({ items, rowsById });
    if (missingConversion) {
      notify(`No hay conversion a kg para ${missingConversion.metricUnit}.`);
      return;
    }
    setSaving(true);
    try {
      await createInventoryMerma(branchId, { items });
      notify('Merma registrada.');
      onSaved?.();
      onClose();
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudo registrar merma.');
    } finally {
      setSaving(false);
    }
  };

  const enteredCount = serializeItems().length;

  return (
    <div className="fixed inset-0 z-[1600] bg-slate-950/50 px-3 py-4 md:pl-20 md:pr-6 overflow-y-auto">
      <div className="mx-auto max-w-7xl bg-white rounded-lg shadow-xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Merma masiva</p>
            <h2 className="text-xl font-semibold text-slate-900">Registrar perdidas</h2>
            <p className="text-sm text-slate-600">{enteredCount} filas listas para guardar</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <FiX /> Cerrar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:bg-red-300"
            >
              <FiTrendingDown /> {saving ? 'Guardando...' : 'Guardar merma'}
            </button>
          </div>
        </div>

        <div className="p-5 border-b border-slate-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="relative w-full md:max-w-md">
            <FiSearch className="absolute left-3 top-3 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
              placeholder="Buscar producto o variedad"
            />
          </label>
          <div className="text-sm text-slate-600">{inventoryRows.length} variedades</div>
        </div>

        <div className="md:hidden divide-y divide-slate-200">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <InlineLoading label="Cargando inventario" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              No hay productos para mostrar.
            </div>
          ) : filteredRows.map((row, index) => {
            const entry = entries[row.varietyId] || {
              quantity: '',
              metricUnit: getDefaultUnit(row),
              reason: 'Merma',
              note: '',
            };
            const entered = isQuantityEntered(entry.quantity);
            const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
            const unitOptions = row.unitOptions?.length
              ? row.unitOptions
              : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
            const isExpanded = String(expandedVarietyId) === String(row.varietyId);
            return (
              <article key={row.varietyId} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                <button
                  type="button"
                  onClick={() => setExpandedVarietyId(isExpanded ? null : row.varietyId)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                >
                  <ProductIdentity row={row} imageSize="h-12 w-12" />
                  <div className="flex shrink-0 items-center gap-2 text-right">
                    <div>
                      <p className="text-[11px] text-slate-500">Disponible</p>
                      <p className="text-sm font-semibold text-slate-900">{formatKg(row.availableKg)}</p>
                    </div>
                    {isExpanded ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <DetailLine label="Equiv. kg" value={
                        entered && kgPreview === null ? (
                          <span className="inline-flex items-center gap-1 text-amber-700"><FiAlertTriangle /> Sin conversion</span>
                        ) : entered ? formatKg(kgPreview) : '-'
                      } />
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Merma
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          value={entry.quantity}
                          onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-right text-sm"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Unidad
                        <select
                          value={entry.metricUnit}
                          onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        >
                          {unitOptions.map((option) => (
                            <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                              {option.metricUnit}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Motivo
                        <input
                          value={entry.reason}
                          onChange={(event) => updateEntry(row, { reason: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        />
                      </label>
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nota
                        <input
                          value={entry.note}
                          onChange={(event) => updateEntry(row, { note: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Producto</th>
                <th className="px-4 py-3 text-right">Disponible</th>
                <th className="px-4 py-3 text-right">Merma</th>
                <th className="px-4 py-3 text-left">Unidad</th>
                <th className="px-4 py-3 text-right">Equiv. kg</th>
                <th className="px-4 py-3 text-left">Motivo</th>
                <th className="px-4 py-3 text-left">Nota</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <InlineLoading label="Cargando inventario" />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    No hay productos para mostrar.
                  </td>
                </tr>
              ) : filteredRows.map((row) => {
                const entry = entries[row.varietyId] || {
                  quantity: '',
                  metricUnit: getDefaultUnit(row),
                  reason: 'Merma',
                  note: '',
                };
                const entered = isQuantityEntered(entry.quantity);
                const kgPreview = entered ? getInventoryKgPreview(row, entry.quantity, entry.metricUnit) : null;
                const unitOptions = row.unitOptions?.length
                  ? row.unitOptions
                  : [{ metricUnit: 'kg', metricUnitKey: 'kg' }];
                return (
                  <tr key={row.varietyId} className="odd:bg-white even:bg-slate-50/70 hover:bg-slate-100">
                    <td className="px-4 py-3 min-w-56">
                      <ProductIdentity row={row} />
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{formatKg(row.availableKg)}</td>
                    <td className="px-4 py-3 min-w-32">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={entry.quantity}
                        onChange={(event) => updateEntry(row, { quantity: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1 text-right"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-36">
                      <select
                        value={entry.metricUnit}
                        onChange={(event) => updateEntry(row, { metricUnit: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      >
                        {unitOptions.map((option) => (
                          <option key={option.metricUnitKey || option.metricUnit} value={option.metricUnit}>
                            {option.metricUnit}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entered && kgPreview === null ? (
                        <span className="inline-flex items-center justify-end gap-1 text-amber-700">
                          <FiAlertTriangle /> Sin conversion
                        </span>
                      ) : entered ? formatKg(kgPreview) : '-'}
                    </td>
                    <td className="px-4 py-3 min-w-44">
                      <input
                        value={entry.reason}
                        onChange={(event) => updateEntry(row, { reason: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      />
                    </td>
                    <td className="px-4 py-3 min-w-56">
                      <input
                        value={entry.note}
                        onChange={(event) => updateEntry(row, { note: event.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-2 py-1"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const InventoryPage = () => {
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  const canUpdateStock = hasPermission(user, 'stock.update');
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({
    availableKg: 0,
    negativeKg: 0,
    mermaKg: 0,
    unresolvedMovements: 0,
    wholesaleEstimateAmount: 0,
    wholesaleEstimateMissingCount: 0,
    wholesaleEstimateRowCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [status, setStatus] = useState('all');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [countSessions, setCountSessions] = useState([]);
  const [countSessionsLoading, setCountSessionsLoading] = useState(false);
  const [countWorkspaceSessionId, setCountWorkspaceSessionId] = useState(null);
  const [countWorkspaceOpen, setCountWorkspaceOpen] = useState(false);
  const [mermaWorkspaceOpen, setMermaWorkspaceOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [expandedVarietyId, setExpandedVarietyId] = useState(null);

  const rowCategories = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (!row.categoryId || !row.categoryName) return;
      map.set(String(row.categoryId), row.categoryName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);
  const categories = categoryOptions.length ? categoryOptions : rowCategories;
  const categoryIdsParam = useMemo(
    () => (selectedCategoryIds.length ? selectedCategoryIds.join(',') : undefined),
    [selectedCategoryIds]
  );

  const fetchSummary = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const response = await getInventorySummary(branchId, {
        page,
        pageSize: PAGE_SIZE,
        search,
        categoryIds: categoryIdsParam,
        status,
      });
      const normalized = normalizePaginatedResponse(response);
      setRows(normalized.data || []);
      setTotalRows(normalized.total || 0);
      setTotalPages(normalized.totalPages || 1);
      setKpis(response.kpis || {});
    } catch (error) {
      console.error('Error fetching inventory:', error);
      notify(error?.response?.data?.message || 'No se pudo cargar inventario.');
    } finally {
      setLoading(false);
    }
  }, [branchId, categoryIdsParam, page, search, status]);

  const fetchCountSessions = useCallback(async () => {
    if (!branchId) return;
    setCountSessionsLoading(true);
    try {
      const response = await getInventoryCountSessions(branchId, {
        pageSize: 8,
        sortBy: 'countedAt',
        sortOrder: 'desc',
      });
      const normalized = normalizePaginatedResponse(response);
      setCountSessions(normalized.data || []);
    } catch (error) {
      setCountSessions([]);
    } finally {
      setCountSessionsLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchCountSessions();
  }, [fetchCountSessions]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories({ branchId });
        setCategoryOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        setCategoryOptions([]);
      }
    };
    if (branchId) fetchCategories();
  }, [branchId]);

  useEffect(() => {
    setPage(1);
    setExpandedVarietyId(null);
  }, [search, categoryIdsParam, status]);

  const refreshInventoryViews = async () => {
    await Promise.all([
      fetchSummary(),
      fetchCountSessions(),
    ]);
  };

  const openMovements = async (row) => {
    setSelectedRow(row);
    setMovementsLoading(true);
    try {
      const response = await getInventoryMovements(branchId, {
        varietyId: row.varietyId,
        pageSize: 0,
      });
      const normalized = normalizePaginatedResponse(response);
      setMovements(normalized.data || []);
    } catch (error) {
      notify(error?.response?.data?.message || 'No se pudieron cargar movimientos.');
    } finally {
      setMovementsLoading(false);
    }
  };

  const openNewCount = () => {
    setCountWorkspaceSessionId(null);
    setCountWorkspaceOpen(true);
  };

  const openExistingCount = (session) => {
    setCountWorkspaceSessionId(session.id);
    setCountWorkspaceOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
            <p className="text-sm text-slate-600">Sucursal {branchId}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setEmailModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <FiMail /> Enviar PDF
            </button>
            {canUpdateStock && (
              <>
                <button
                  type="button"
                  onClick={openNewCount}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <FiPlus /> Nuevo conteo
                </button>
                <button
                  type="button"
                  onClick={() => setMermaWorkspaceOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <FiTrendingDown /> Merma masiva
                </button>
              </>
            )}
            <button
              type="button"
              onClick={refreshInventoryViews}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <FiRefreshCw /> Actualizar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Disponible</p>
            <p className="text-2xl font-bold text-slate-900">{formatKg(kpis.availableKg)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Negativo</p>
            <p className="text-2xl font-bold text-red-700">{formatKg(kpis.negativeKg)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Merma</p>
            <p className="text-2xl font-bold text-slate-900">{formatKg(kpis.mermaKg)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sin conversion</p>
            <p className="text-2xl font-bold text-amber-700">{kpis.unresolvedMovements || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estimado mayorista</p>
            <p className="flex items-center gap-1 text-2xl font-bold text-emerald-700">
              <FiDollarSign className="h-5 w-5" />
              {formatInventoryCurrency(kpis.wholesaleEstimateAmount).replace('$', '')}
            </p>
            {Number(kpis.wholesaleEstimateMissingCount || 0) > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                {kpis.wholesaleEstimateMissingCount} variedades sin precio convertible
              </p>
            )}
          </div>
        </div>

        <CountSessionsPanel
          sessions={countSessions}
          loading={countSessionsLoading}
          onOpen={openExistingCount}
        />

        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="p-4 border-b border-slate-200 grid grid-cols-1 gap-3 md:grid-cols-[1fr_320px_160px] md:items-start">
            <label className="relative">
              <FiSearch className="absolute left-3 top-3 text-slate-400" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2"
                placeholder="Buscar producto o variedad"
              />
            </label>
            <CategoryMultiSelect
              categories={categories}
              selectedCategoryIds={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              label=""
              placeholder="Todas las categorias"
            />
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              <option value="ok">OK</option>
              <option value="negative">Negativo</option>
              <option value="warning">Revisar</option>
            </select>
          </div>

          <div className="md:hidden divide-y divide-slate-200">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <InlineLoading label="Cargando inventario" />
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No hay inventario para mostrar.
              </div>
            ) : rows.map((row, index) => {
              const statusMeta = getInventoryStatusMeta(row);
              const isExpanded = String(expandedVarietyId) === String(row.varietyId);
              return (
                <article key={row.varietyId} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                  <button
                    type="button"
                    onClick={() => setExpandedVarietyId(isExpanded ? null : row.varietyId)}
                    className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
                  >
                    <ProductIdentity row={row} imageSize="h-12 w-12" />
                    <div className="flex shrink-0 items-center gap-2 text-right">
                      <div>
                        <p className="text-[11px] text-slate-500">Disponible</p>
                        <p className={`text-sm font-semibold ${row.availableKg < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                          {formatKg(row.availableKg)}
                        </p>
                      </div>
                      {isExpanded ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <DetailLine label="Unidades" value={formatStockedUnits(row.stockedUnits)} />
                        <DetailLine label="Vendido" value={formatKg(row.soldKg)} />
                        <DetailLine label="Merma" value={formatKg(row.mermaKg)} />
                        <DetailLine label="Ultimo conteo" value={row.lastCount ? formatKg(row.lastCount.countedKg) : '-'} />
                      </div>
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                        <button
                          type="button"
                          onClick={() => openMovements(row)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                        >
                          <FiClipboard /> Mov.
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Producto</th>
                  <th className="px-4 py-3 text-right">Disponible kg</th>
                  <th className="px-4 py-3 text-left">Unidades</th>
                  <th className="px-4 py-3 text-right">Vendido kg</th>
                  <th className="px-4 py-3 text-right">Merma kg</th>
                  <th className="px-4 py-3 text-right">Ultimo conteo</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center">
                      <InlineLoading label="Cargando inventario" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                      No hay inventario para mostrar.
                    </td>
                  </tr>
                ) : rows.map((row) => {
                  const statusMeta = getInventoryStatusMeta(row);
                  return (
                    <tr key={row.varietyId} className="odd:bg-white even:bg-slate-50/70 hover:bg-slate-100">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openMovements(row)}
                          className="text-left"
                        >
                          <ProductIdentity row={row} />
                        </button>
                      </td>
                      <td className={`px-4 py-3 text-right font-semibold ${row.availableKg < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                        {formatKg(row.availableKg)}
                      </td>
                      <td className="px-4 py-3 text-slate-700 min-w-56">
                        {formatStockedUnits(row.stockedUnits)}
                      </td>
                      <td className="px-4 py-3 text-right">{formatKg(row.soldKg)}</td>
                      <td className="px-4 py-3 text-right">{formatKg(row.mermaKg)}</td>
                      <td className="px-4 py-3 text-right">
                        {row.lastCount ? formatKg(row.lastCount.countedKg) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openMovements(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                          >
                            <FiClipboard /> Mov.
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-slate-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-slate-600">
            <span>{totalRows} variedades</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                className="px-3 py-1 rounded border border-slate-300 disabled:opacity-50"
              >
                Anterior
              </button>
              <span>Pagina {page} de {Math.max(totalPages, 1)}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-3 py-1 rounded border border-slate-300 disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedRow && (
        <div className="fixed inset-y-0 right-0 z-40 w-full max-w-xl bg-white shadow-2xl border-l border-slate-200 flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Movimientos</p>
              <h2 className="text-lg font-semibold text-slate-900">{getRowLabel(selectedRow)}</h2>
            </div>
            <button
              type="button"
              onClick={() => setSelectedRow(null)}
              className="text-slate-500 hover:text-slate-900"
              aria-label="Cerrar"
            >
              x
            </button>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-200">
            {movementsLoading ? (
              <div className="p-6 text-center"><InlineLoading label="Cargando movimientos" /></div>
            ) : movements.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">Sin movimientos.</p>
            ) : movements.map((movement) => {
              const signed = Number(movement.signedQuantityKg);
              const unresolved = movement.unresolvedReason || !Number.isFinite(signed);
              return (
                <div key={movement.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {movement.type === 'stock_in' && 'Ingreso'}
                        {movement.type === 'sale' && 'Venta'}
                        {movement.type === 'transfer_out' && 'Traslado'}
                        {movement.type === 'merma' && 'Merma'}
                        {movement.type === 'count_adjustment' && 'Ajuste conteo'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {movement.createdAt ? new Date(movement.createdAt).toLocaleString('es-CL') : '-'}
                      </p>
                      {movement.reason && <p className="text-xs text-slate-600 mt-1">{movement.reason}</p>}
                      {movement.note && <p className="text-xs text-slate-600 mt-1">{movement.note}</p>}
                    </div>
                    <div className={`text-right font-semibold ${signed < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                      {unresolved ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <FiAlertTriangle /> Sin conversion
                        </span>
                      ) : (
                        `${signed > 0 ? '+' : ''}${formatKg(signed)}`
                      )}
                      <p className="text-xs font-normal text-slate-500">
                        {formatInventoryQuantity(movement.inputQuantity)} {movement.metricUnit || ''}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {countWorkspaceOpen && (
        <InventoryCountWorkspace
          branchId={branchId}
          sessionId={countWorkspaceSessionId}
          user={user}
          categoryOptions={categories}
          initialCategoryIds={countWorkspaceSessionId ? [] : selectedCategoryIds}
          onClose={() => setCountWorkspaceOpen(false)}
          onSaved={refreshInventoryViews}
        />
      )}

      {mermaWorkspaceOpen && (
        <BulkMermaWorkspace
          branchId={branchId}
          onClose={() => setMermaWorkspaceOpen(false)}
          onSaved={refreshInventoryViews}
        />
      )}

      {emailModalOpen && (
        <InventoryEmailModal
          branchId={branchId}
          search={search}
          status={status}
          selectedCategoryIds={selectedCategoryIds}
          categories={categories}
          totalRows={totalRows}
          kpis={kpis}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
};

export default InventoryPage;
